import type { Theme } from '@mui/material/styles';
import type { OverridesStyleRules } from '@mui/material/styles/overrides';
import type { DataGridProProps, GridRowHeightReturnValue } from '@mui/x-data-grid-pro';
import { ColumnTypeIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { COLORS } from './colors';

export const SIZES = {
  defaultRowHeight: '52px',
  defaultRowFooterHeight: '40px',
  defaultFontWeight: 400,
  columnHeaderHeight: 33,
  // Width of MUI DataGrid's synthetic __reorder__ column (from GRID_REORDER_COL_DEF).
  // This app does not define a custom __reorder__ column, so the MUI default always applies.
  reorderColumnWidth: 50,
};

const STYLES = {
  blueBorderStyle: `2px solid ${COLORS.HoverNFocus}`,
  focusedBoxShadow: '0px 1px 2px rgba(0, 0, 0, 0.30), 0px 1px 3px rgba(0, 0, 0, 0.15)',
};

export const SPACING = {
  sm: '10px',
  md: '16px',
};

const getRowHeight = (): GridRowHeightReturnValue => {
  return 'auto';
};

const EDITING_CELL = '.MuiDataGrid-cell--editing';

export const tableColumnTypes = {
  TEXT: ColumnTypeIdentifier.TEXT,
  MULTIPLIER: ColumnTypeIdentifier.MULTIPLIER,
  PRICE: ColumnTypeIdentifier.PRICE,
  SUBTOTAL: ColumnTypeIdentifier.SUBTOTAL,
};

export const MuiDataGrid: {
  defaultProps?: Partial<DataGridProProps<any>>;
  styleOverrides?: Partial<OverridesStyleRules>;
} = {
  defaultProps: {
    columnHeaderHeight: SIZES.columnHeaderHeight,
    showColumnVerticalBorder: true,
    hideFooter: false,
    hideFooterRowCount: true,
    hideFooterPagination: true,
    hideFooterSelectedRowCount: true,
    disableColumnMenu: true,
    disableColumnSorting: true,
    getRowHeight,
    autoHeight: true,
    getRowId: (row) => {
      return row.id;
    },
    columnVisibilityModel: { id: false },
  },
  styleOverrides: {
    root: {
      borderRadius: 0,
      fontWeight: SIZES.defaultFontWeight,
      '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
        borderBottom: 'none',
      },
      '.MuiDataGrid-columnSeparator--resizable.MuiDataGrid-columnSeparator--resizing': {
        color: COLORS.borderColor,
      },
      'div[class*="MuiDataGrid-footerContainer"]': {
        display: 'none',
      },
      ':not(&.py-contentful-state) .MuiDataGrid-container--top': {
        position: 'absolute',
        zIndex: 0,
        visibility: 'hidden',
      },
      '&.py-selected-state': {
        zIndex: -1,
      },
      '&.py-contentful-state': {
        zIndex: 'auto',
        '.MuiDataGrid-cell.py-selected-column-cell': {
          borderLeft: STYLES.blueBorderStyle,
          borderRight: STYLES.blueBorderStyle,
          backgroundColor: COLORS.columnHoverBgTransparent,
        },
        '.MuiDataGrid-row.py-body-row:not(:has(~ .MuiDataGrid-row.py-body-row)) .MuiDataGrid-cell.py-selected-column-cell': {
          borderBottom: STYLES.blueBorderStyle,
        },
        '.Mui-selected': {
          boxShadow: STYLES.focusedBoxShadow,
        },
        [`${EDITING_CELL} .editable-cell-wrapper`]: {
          position: 'relative',
        },
        [`${EDITING_CELL} .editable-cell-wrapper[data-placeholder]::before`]: {
          content: 'attr(data-placeholder)',
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: COLORS.placeholderGrey,
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '1rem',
        },
        [`${EDITING_CELL} .editable-cell-wrapper[data-column-type="${ColumnTypeIdentifier.PRICE}"][data-placeholder]::before,
          ${EDITING_CELL} .editable-cell-wrapper[data-column-type="${ColumnTypeIdentifier.MULTIPLIER}"][data-placeholder]::before,
          ${EDITING_CELL} .editable-cell-wrapper[data-column-type="${ColumnTypeIdentifier.SUBTOTAL}"][data-placeholder]::before`]: {
          textAlign: 'right',
        },
      },
      '.MuiDataGrid-filler, .MuiDataGrid-scrollbar': {
        display: 'none',
      },
      'input[type="number"]': {
        textAlign: 'right',
      },
      '.py-custom-input': {
        border: 'none',
        color: COLORS.cellColor,
        paddingLeft: SPACING.sm,
        paddingRight: SPACING.sm,
        fontSize: '1rem',
        background: 'transparent',
        '&:focus-visible': {
          outline: 'none',
          border: 'none',
        },
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          margin: 5,
        },
      },
      '.py-custom-cell-wrapper': {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        input: {
          width: '100%',
          paddingLeft: SPACING.sm,
          paddingRight: SPACING.sm,
        },
      },
      '.py-number-input-no-arrow': {
        '& input[type=number]': {
          MozAppearance: 'text field',
          '&::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '&::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        },
      },
    },
    columnHeaderRow: (props: unknown) => ({
      '--DataGrid-containerBackground': (props as { theme: Theme }).theme.palette.primary.main,
      color: (props as { theme: Theme }).theme.palette.primary.contrastText,
      fontSize: '14px',
      lineHeight: 24,
      '.MuiDataGrid-withBorderColor': {
        borderRight: '1px solid rgba(255, 255, 255, 0.15)',
        borderBottom: 'none',
      },
    }),

    columnHeader: {
      borderLeft: '2px solid transparent',
      borderRight: '2px solid transparent',
      paddingTop: '6px',
      '.MuiDataGrid-columnHeaderTitleContainer': {
        paddingLeft: '10px',
        paddingRight: '10px',
        gap: '4px',
      },
      '&.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
        paddingRight: 0,
      },
      '&:focus, &:focus-within': {
        outline: 'none',
      },
      '.py-contentful-state &:focus, .py-contentful-state &:focus-within, &.py-column-selected': {
        overflow: 'hidden',
        borderLeft: `2px solid ${COLORS.HoverNFocus}`,
        borderRight: `2px solid ${COLORS.HoverNFocus}`,
        backgroundColor: COLORS.HoverNFocus,
        color: COLORS.tableTextColor,
        outline: 'none',
        '& svg': { color: COLORS.tableTextColor },
      },
      '.py-cellWidthHandler': {
        position: 'absolute',
        width: '2px',
        left: 0,
        top: 0,
        height: '100%',
        '&.activeHoverColumn::before': {
          backgroundColor: '#4FCEFD',
          cursor: 'col-resize',
        },
        '&::before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          left: '-2px',
          top: '-1px',
          width: '2px',
          height: `calc(100% + 2px)`,
          backgroundColor: 'transparent',
          zIndex: 20,
        },
      },
    },
    row: {
      minHeight: `${SIZES.defaultRowHeight} !important`,
      backgroundColor: 'var(--table-row-default-background-color)',
      '.MuiDataGrid-cell.py-cellHandlerWrapper': {
        borderLeft: '1px solid var(--rowBorderColor)',
        position: 'relative',
        overflow: 'visible',
        '.py-cellWidthHandler': {
          position: 'absolute',
          width: '2px',
          left: 0,
          top: 0,
          height: '100%',
          '&.activeHoverColumn::before': {
            backgroundColor: '#4FCEFD',
            cursor: 'col-resize',
          },
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: '-2px',
            top: '-1px',
            width: '2px',
            height: `calc(100% + 2px)`,
            backgroundColor: 'transparent',
            zIndex: 20,
          },
        },
        '.py-tableContextMenuTrigger': {
          visibility: 'hidden',
          opacity: '0',
          transition: 'all 200ms ease-in-out',
        },
        '&:focus-within .py-tableContextMenuTrigger, :hover .py-tableContextMenuTrigger': {
          visibility: 'visible',
          opacity: '1',
        },
      },
      '&.py-footer-row .MuiDataGrid-cell': {
        minHeight: `${SIZES.defaultRowFooterHeight}`,
      },
      '&:hover, &.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: 'var(--table-row-default-background-color)',
      },
      '&.py-selected-row': {
        '& .MuiDataGrid-cell': {
          borderTop: `2px solid ${COLORS.HoverNFocus}`,
          borderBottom: `2px solid ${COLORS.HoverNFocus}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: COLORS.HoverNFocus,
          zIndex: 2,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: COLORS.HoverNFocus,
          zIndex: 2,
          pointerEvents: 'none',
        },
        '.MuiDataGrid-cell.MuiDataGrid-rowReorderCellContainer': {
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: COLORS.HoverNFocus,
          '.MuiDataGrid-rowReorderCell': {
            position: 'relative',
            zIndex: 1,
          },
        },
      },
      '&.py-focused-row:not(.py-header-row)': {
        '.MuiDataGrid-cell.MuiDataGrid-rowReorderCellContainer': {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '10%',
            bottom: '10%',
            left: '-6px',
            right: '-6px',
            borderRadius: '100px',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        },
      },
    },
    cell: {
      borderBottom: `1px solid ${COLORS.divider}`,
      borderLeft: `1px solid ${COLORS.divider}`,
      borderRight: `1px solid ${COLORS.divider}`,
      display: 'flex',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      alignItems: 'start',

      '& > div, .tiptap.ProseMirror': {
        width: '100%',

        '&.ProseMirror-focused': {
          outlineColor: 'transparent',
        },
      },

      '&[data-field="optionalColumn"]': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '.Mui-checked.py-row-optional': {
        color: '#2196F3',
      },
      'input[type="number"]': {
        textAlign: 'right',
      },
      '&.MuiDataGrid-rowReorderCellContainer': {
        alignItems: 'unset',
        wordBreak: 'unset',
        '&:focus, &:focus-within': {
          outline: 'none',
        },
      },
      '&:focus, &.MuiDataGrid-cell--editing:focus-within, &:focus-within': {
        outline: STYLES.blueBorderStyle,
        outlineOffset: '-2px',
      },
      '&:has(.py-input-error)': {
        '&:focus, &.MuiDataGrid-cell--editing:focus-within, &:focus-within': {
          outline: `2px solid ${COLORS.error}`,
        },
      },
      '&.MuiDataGrid-cell': {
        '&--textRight': {
          '.editable-cell-wrapper': {
            justifyContent: 'flex-end',
          },
          '.tiptap p': {
            textAlign: 'right',
          },
        },
        '&--editing, &--editing:focus-within': {
          backgroundColor: 'unset',
          paddingLeft: 10,
          paddingRight: 10,
        },
        '.py-mui-tiptap-input': {
          borderRadius: 0,
          padding: 0,
          width: '100%',
          height: '100%',
          minHeight: '16px',
          '& p': {
            '&.is-editor-empty:first-of-type::before': {
              display: 'none',
            },
          },
        },
        '.py-mui-tiptap-input-preview': {
          border: 'none',
          padding: 0,
          minHeight: 'initial',
          alignSelf: 'stretch',
          '& *::selection': {
            backgroundColor: '#004053',
          },
        },
      },
      '&.py-total-cell': {
        justifyContent: 'flex-end',
        textAlign: 'right',
      },
      '.py-custom-input': {
        border: 'none',
        color: COLORS.cellColor,
        fontSize: '1rem',
        textAlign: 'inherit',
        '&:focus-visible': {
          outline: 'none',
          border: 'none',
        },
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          margin: 5,
        },
        '&::placeholder': {
          color: COLORS.placeholderGrey,
          fontStyle: 'normal',
          fontWeight: 400,
          opacity: 1,
        },
      },
      '.py-number-input-no-arrow': {
        border: 'none',
        background: 'transparent',

        color: COLORS.cellColor,
        fontSize: '1rem',
        '&:focus-visible': {
          outline: 'none',
          border: 'none',
        },
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          display: 'none',
        },
        '&[type="number"]': {
          MozAppearance: 'textfield',
          background: 'transparent',
        },
        '&::placeholder': {
          color: COLORS.placeholderGrey,
          fontStyle: 'normal',
          fontWeight: 400,
          opacity: 1,
        },
      },
      '&.py-hide-cell': {
        backgroundColor: COLORS.greyedBackgroundColor,
        input: {
          backgroundColor: COLORS.greyedBackgroundColor,
        },
      },

      '.py-value-formatter': {
        marginLeft: `-${SPACING.sm}`,
      },
      '> div': {
        paddingTop: '12px',
        paddingBottom: '12px',
        '.MuiCheckbox-root': {
          marginTop: '-12px',
          marginBottom: '-12px',
        },
      },
      '&.py-is-numeric-cell': {
        alignItems: 'center',
      },
    },
    rowReorderCell: {
      color: 'rgba(0, 0, 0, 0.38)',
    },
    rowReorderCellPlaceholder: {
      fontWeight: SIZES.defaultFontWeight,
    },
  },
};
